import { Component, type ErrorInfo, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { toAppError } from '@/lib/errors'

interface Props extends WithTranslation {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Top-level error boundary.
 *
 * A family archive is often the only remaining copy of a photograph or a
 * transcription. A white screen with no explanation is not an acceptable
 * failure mode for that, so we always render something with a recovery
 * path and never lose the user's place.
 *
 * Uses the `withTranslation` HOC because error boundaries must be class
 * components, and a class cannot call `useTranslation`. Translating the
 * failure screen matters more than most: it is the one moment a user is
 * least able to guess what an unfamiliar word means.
 */
class ErrorBoundaryInner extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // M2 replaces this with a real reporter. Logging the component stack
    // is what makes a user's "it broke" actionable.
    console.error('Unhandled error', error, info.componentStack)
  }

  private readonly reset = () => {
    this.setState({ error: null })
  }

  override render() {
    const { error } = this.state
    const { t, fallback, children } = this.props

    if (!error) return children
    if (fallback) return fallback(error, this.reset)

    const appError = toAppError(error)

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="font-serif text-2xl">{t('error.title')}</h1>
        <p className="text-muted-foreground max-w-prose text-sm">
          {t(`errors:${appError.messageKey}`, { defaultValue: t('errors:unknown') })}
        </p>
        <div className="flex gap-2">
          <Button onClick={this.reset}>{t('action.retry')}</Button>
          <Button
            variant="outline"
            onClick={() => {
              window.location.href = '/'
            }}
          >
            {t('action.goHome')}
          </Button>
        </div>
      </div>
    )
  }
}

export const ErrorBoundary = withTranslation('common')(ErrorBoundaryInner)
